import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
// react-markdown v10 ships React 19 types which are incompatible with React 18's JSX types.
import _ReactMarkdown from 'react-markdown';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactMarkdown = _ReactMarkdown as any;
import remarkGfm from 'remark-gfm';
import { useGraphEntities } from '../contexts/GraphEntitiesContext';
import { useAuth } from '../hooks/useAuth';
import { EntityChip } from './EntityChip';
import { EvidenceTd } from './markdownCells';
import { trpc } from '../api/trpc';
import { wsManager } from '../api/websocket';
import type { WebSocketEvent, ReasoningStep, ToolCallRecord } from '@cade/shared-types';
import { StreamingMessage } from './StreamingMessage';
import { CadeAvatar } from './CadeAvatar';
import { ReasoningTrace } from './ReasoningTrace';
import { ImagePreviewModal } from './ImagePreviewModal';
import { NextStepButtons } from './NextStepButtons';
import { WriteConfirmation } from './WriteConfirmation';
import { TypingIndicator } from './TypingIndicator';
import { MessageSkeleton } from './MessageSkeleton';

interface Props {
  conversationId: string;
  graphEntityMap?: Map<string, { id: string; type: string; label: string; aliases?: string[] }>;
  onEntityClick?: (nodeId: string) => void;
}

interface ToolCallEntry {
  toolName: string;
  toolUseId: string;
  status: 'running' | 'success' | 'error';
  summary?: string;
  inputPayload?: Record<string, unknown>;
  queryLanguage?: string;
  resultSummary?: string;
  latencyMs?: number;
}

interface TraceEntry {
  type: 'thinking' | 'tool';
  // For thinking: the accumulated text for this segment
  text?: string;
  // For tool: the toolUseId to look up in toolCalls map
  toolUseId?: string;
}

interface StreamState {
  messageId: string;
  content: string;
  reasoning: string[];
  reasoningText: string;
  done: boolean;
  toolCalls: Map<string, ToolCallEntry>;
  traceEntries: TraceEntry[];
}

// Parse numbered next steps from end of AI message.
// A numbered list item starts with `\n<digit>.` and continues until the next
// numbered item or end-of-string. We only treat the trailing numbered block as
// suggestions — the block must be at the very end of the message.
function parseNextSteps(content: string): { steps: string[]; stripFrom: number } {
  // Match all numbered list items (including multi-line continuations)
  const itemRegex = /^\d+\.\s+(.+?)(?=\n\d+\.\s|\s*$)/gms;
  const allItems: { text: string; start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = itemRegex.exec(content)) !== null) {
    // Collapse the captured text (may span lines) into a single line, strip markdown bold
    const text = (m[1] ?? '').replace(/\n\s*/g, ' ').replace(/\*{1,2}/g, '').trim();
    allItems.push({ text, start: m.index, end: m.index + m[0].length });
  }

  if (allItems.length === 0) return { steps: [], stripFrom: -1 };

  // Only use the trailing block — the last item must end near the content end
  const contentEnd = content.trimEnd().length;
  const lastItem = allItems[allItems.length - 1];
  if (!lastItem || lastItem.end < contentEnd - 2) return { steps: [], stripFrom: -1 };

  // Walk backwards to find the contiguous trailing block of numbered items
  // (they should be consecutive — no large gap between them)
  const trailing: { text: string; start: number }[] = [];
  for (let i = allItems.length - 1; i >= 0; i--) {
    const item = allItems[i];
    if (!item) break;
    trailing.unshift({ text: item.text, start: item.start });
    // Stop if the gap to the previous item is large (separate section)
    if (i > 0) {
      const prev = allItems[i - 1];
      if (!prev) break;
      const gap = item.end - prev.end;
      // If items are far apart, they belong to different lists
      if (gap > item.text.length + 100) break;
    }
  }

  // stripFrom = where the trailing block begins, so the renderer can drop the
  // raw numbered list (it's shown as next-step buttons instead — no duplicate).
  // Render ALL trailing items as buttons: stripFrom removes the whole block, so
  // capping the buttons (e.g. .slice(0,3)) would silently drop items 4+ from
  // both the markdown and the buttons.
  return { steps: trailing.map((t) => t.text), stripFrom: trailing[0]?.start ?? -1 };
}

// Parse file attachments from user message content
function parseAttachments(content: string): { text: string; files: string[] } {
  const files: string[] = [];
  const text = content.replace(/\[Attached:\s*(.+?)\]/g, (_, name: string) => {
    files.push(name.trim());
    return '';
  }).trim();
  return { text, files };
}

// Attachment card in sent messages — fetches file metadata and renders as a square card
function MessageAttachmentCard({ fileId, filename, onImagePreview }: { fileId?: string; filename: string; onImagePreview: (url: string, name: string) => void }) {
  const [fileData, setFileData] = useState<{ url: string; mimeType: string; textContent?: string } | null>(null);
  const utils = trpc.useUtils();

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    utils.files.download.fetch({ fileId }).then(async (result) => {
      if (cancelled) return;
      const data: { url: string; mimeType: string; textContent?: string } = { url: result.url, mimeType: result.mimeType };
      // For text files, fetch content for snippet preview
      if (result.mimeType.startsWith('text/') || result.mimeType === 'application/json' || result.mimeType === 'application/xml') {
        try {
          const resp = await fetch(result.url);
          if (resp.ok) data.textContent = (await resp.text()).slice(0, 5000);
        } catch { /* ignore */ }
      }
      if (!cancelled) setFileData(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [fileId, utils]);

  const isImage = fileData?.mimeType.startsWith('image/');
  const handleClick = () => {
    if (!fileData) return;
    if (isImage) onImagePreview(fileData.url, filename);
    else if (fileData.textContent) { /* handled via state in parent */ }
    else window.open(fileData.url, '_blank');
  };

  return (
    <div className="attachment-card message-attachment-card" onClick={handleClick}>
      {fileData && isImage ? (
        <img src={fileData.url} alt={filename} />
      ) : fileData?.textContent ? (
        <div className="attachment-card-text">{fileData.textContent.slice(0, 200)}</div>
      ) : (
        <div className="attachment-card-file">
          <svg className="attachment-card-file-icon" width="20" height="20" viewBox="0 0 16 16" fill="none">
            <path d="M9 1.5H4.5A1.5 1.5 0 003 3v10a1.5 1.5 0 001.5 1.5h7A1.5 1.5 0 0013 13V5.5L9 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
            <path d="M9 1.5V5.5H13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="attachment-card-file-name">{filename}</span>
        </div>
      )}
    </div>
  );
}

// Parse write action blocks from AI message
function parseWriteAction(content: string): { action: string; target: string; description: string } | null {
  const actionMatch = content.match(/\*\*Write Action:\*\*\s*(.+)/);
  const targetMatch = content.match(/\*\*Target:\*\*\s*(.+)/);
  const descMatch = content.match(/\*\*Description:\*\*\s*(.+)/);

  if (actionMatch?.[1] && targetMatch?.[1]) {
    return {
      action: actionMatch[1].trim(),
      target: targetMatch[1].trim(),
      description: descMatch?.[1]?.trim() ?? '',
    };
  }
  return null;
}

// Render note content with @mention highlights
function renderNoteContent(content: string, mentions: Array<{ userId: string; name: string }>): React.ReactNode {
  if (mentions.length === 0) return <span>{content}</span>;

  // Build a regex that matches any @Name in the content
  const mentionNames = mentions.map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(@(?:${mentionNames.join('|')}))`, 'g');
  const parts = content.split(pattern);

  return (
    <span>
      {parts.map((part, i) => {
        const mention = mentions.find(m => part === `@${m.name}`);
        if (mention) {
          return <span key={i} className="mention-highlight">@{mention.name}</span>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function makeDefaultStreamState(messageId: string): StreamState {
  return { messageId, content: '', reasoning: [], reasoningText: '', done: false, toolCalls: new Map(), traceEntries: [] };
}

// Mono timestamp for Cade message headers — HH:MM:SS, matching the mockup.
export function formatMessageTime(date: string | Date): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

export function MessageList({ conversationId, graphEntityMap, onEntityClick }: Props) {
  const { userId } = useAuth();
  const { graphNodeLabels } = useGraphEntities();
  const { data, isLoading, refetch } = trpc.conversations.get.useQuery({ conversationId });
  const [streaming, setStreaming] = useState<StreamState | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);
  const [typingUsers, setTypingUsers] = useState<Map<string, string>>(new Map());
  const bottomRef = useRef<HTMLDivElement>(null);
  const unreadRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  // Tracks whether the scroll container is pinned to the bottom. If the user
  // scrolls up, this flips false and we stop forcing scroll on every streaming
  // delta — instead we surface a "Jump to latest" pill.
  const isAtBottomRef = useRef(true);
  // Suppresses the scroll handler's atBottom updates while a programmatic
  // smooth-scroll (from jumpToLatest) is mid-animation, so the intermediate
  // scroll events the browser emits during the animation don't flip
  // isAtBottomRef back to false and re-flash the pill.
  const isProgrammaticScrollRef = useRef(false);
  // Flips true once the real .message-list div mounts (after isLoading
  // settles and the empty-state hero is skipped). Used as a dep on the
  // scroll-listener effect so the listener attaches only after the
  // ref-target actually exists.
  const [listMounted, setListMounted] = useState(false);
  const setListRefCallback = useCallback((el: HTMLDivElement | null) => {
    listRef.current = el;
    setListMounted(!!el);
  }, []);
  const lastStreamEventRef = useRef<number>(0);
  const hasScrolledToUnread = useRef(false);
  const hasMarkedAsRead = useRef(false);
  const dividerMsgRef = useRef<string | null | undefined>(undefined);
  const [dividerCleared, setDividerCleared] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const sendMessage = trpc.conversations.sendMessage.useMutation();
  const markAsRead = trpc.conversations.markAsRead.useMutation();
  const utils = trpc.useUtils();

  // Find the current user's lastReadMessageId from participants
  const myParticipant = data?.participants?.find(
    (p: { userId: string }) => p.userId === userId,
  ) as { lastReadMessageId?: string | null } | undefined;
  const liveLastReadMessageId = myParticipant?.lastReadMessageId ?? null;

  // Synchronously capture the divider message ID during render (before effects run).
  // undefined = not yet captured, null = captured but no unread, string = unread divider position
  if (data && dividerMsgRef.current === undefined) {
    dividerMsgRef.current = liveLastReadMessageId;
  }

  useEffect(() => {
    setStreaming(null);
    setTypingUsers(new Map());
    hasScrolledToUnread.current = false;
    hasMarkedAsRead.current = false;
    dividerMsgRef.current = undefined;
    setDividerCleared(false);
    isAtBottomRef.current = true;
    setShowJumpToLatest(false);
  }, [conversationId]);

  // Track whether the user is pinned to the bottom of the scrolling ancestor.
  // We walk up from the list element to find the nearest overflow-y:auto/scroll
  // ancestor so this works regardless of which wrapper actually scrolls.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let scroller: HTMLElement | null = el.parentElement;
    while (scroller && scroller !== document.body) {
      const oy = getComputedStyle(scroller).overflowY;
      if (oy === 'auto' || oy === 'scroll') break;
      scroller = scroller.parentElement;
    }
    if (!scroller || scroller === document.body) {
      scroller = (document.scrollingElement as HTMLElement) ?? document.documentElement;
    }
    scrollerRef.current = scroller;

    const updateAtBottom = () => {
      const s = scrollerRef.current;
      if (!s) return;
      const distance = s.scrollHeight - s.scrollTop - s.clientHeight;
      const atBottom = distance < 80;
      // While jumpToLatest's smooth scroll is animating, the browser fires
      // scroll events with distance>80 for several frames. Don't let those
      // intermediate frames flip the ref back to false (which would
      // re-flash the pill). Clear the flag once the animation lands.
      if (isProgrammaticScrollRef.current) {
        if (atBottom) isProgrammaticScrollRef.current = false;
        return;
      }
      const wasAtBottom = isAtBottomRef.current;
      isAtBottomRef.current = atBottom;
      if (atBottom) {
        setShowJumpToLatest(false);
        // Catch up to the latest content if the user just scrolled back to
        // bottom mid-stream — otherwise the view lags until the next delta.
        if (!wasAtBottom) bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    };

    // Any user-initiated scroll input clears the programmatic flag so it
    // can't get stuck on if jumpToLatest's smooth scroll never reaches the
    // bottom (e.g. user interrupts mid-animation, or content height grows
    // before the animation lands).
    const clearProgrammatic = () => { isProgrammaticScrollRef.current = false; };
    scroller.addEventListener('scroll', updateAtBottom, { passive: true });
    scroller.addEventListener('wheel', clearProgrammatic, { passive: true });
    scroller.addEventListener('touchstart', clearProgrammatic, { passive: true });
    scroller.addEventListener('keydown', clearProgrammatic);
    updateAtBottom();
    return () => {
      scroller!.removeEventListener('scroll', updateAtBottom);
      scroller!.removeEventListener('wheel', clearProgrammatic);
      scroller!.removeEventListener('touchstart', clearProgrammatic);
      scroller!.removeEventListener('keydown', clearProgrammatic);
    };
  }, [conversationId, listMounted]);

  const jumpToLatest = () => {
    // Pin the ref synchronously so a text_delta arriving before the smooth
    // scroll completes doesn't flip the pill back on. The programmatic flag
    // additionally suppresses the scroll handler's intermediate-frame
    // updates while the smooth scroll is animating.
    isAtBottomRef.current = true;
    isProgrammaticScrollRef.current = true;
    setShowJumpToLatest(false);
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Safety net: if content height grows mid-animation and the smooth
    // scroll lands above the new bottom, the scroll handler's
    // distance<80 check never fires and the flag would stay stuck.
    // 700ms outlasts a typical smooth scroll (~300–500ms) without delaying
    // normal interactions.
    window.setTimeout(() => { isProgrammaticScrollRef.current = false; }, 700);
  };

  // Subscribe to stream events + conversation events
  useEffect(() => {
    const unsub = wsManager.subscribe(conversationId, (event: WebSocketEvent) => {
      // Update watchdog timestamp on any streaming event
      if (event.type === 'text_delta' || event.type === 'text_clear' || event.type === 'reasoning_delta' ||
          event.type === 'tool_call_start' || event.type === 'tool_call_input' || event.type === 'tool_call_result') {
        lastStreamEventRef.current = Date.now();
      }
      if (event.type === 'text_delta') {
        setStreaming((prev) =>
          prev
            ? { ...prev, content: prev.content + event.delta }
            : { ...makeDefaultStreamState(event.messageId), content: event.delta },
        );
      } else if (event.type === 'text_clear') {
        setStreaming((prev) =>
          prev ? { ...prev, content: '' } : prev,
        );
      } else if (event.type === 'reasoning_delta') {
        setStreaming((prev) => {
          const base = prev ?? makeDefaultStreamState(event.messageId);
          const entries = [...base.traceEntries];
          const lastEntry = entries[entries.length - 1];
          if (lastEntry?.type === 'thinking') {
            // Append to current thinking segment
            entries[entries.length - 1] = { ...lastEntry, text: (lastEntry.text ?? '') + event.delta };
          } else {
            // Start a new thinking segment
            entries.push({ type: 'thinking', text: event.delta });
          }
          return { ...base, reasoningText: base.reasoningText + event.delta, traceEntries: entries };
        });
      } else if (event.type === 'reasoning_step') {
        setStreaming((prev) =>
          prev
            ? { ...prev, reasoning: [...prev.reasoning, event.step.thinking] }
            : { ...makeDefaultStreamState(event.messageId), reasoning: [event.step.thinking] },
        );
      } else if (event.type === 'tool_call_start') {
        setStreaming((prev) => {
          const base = prev ?? makeDefaultStreamState(event.messageId);
          const updated = new Map(base.toolCalls);
          updated.set(event.toolUseId, { toolName: event.toolName, toolUseId: event.toolUseId, status: 'running' });
          const entries = [...base.traceEntries, { type: 'tool' as const, toolUseId: event.toolUseId }];
          return { ...base, toolCalls: updated, traceEntries: entries };
        });
      } else if (event.type === 'tool_call_input') {
        setStreaming((prev) => {
          if (!prev) return prev;
          const updated = new Map(prev.toolCalls);
          const existing = updated.get(event.toolUseId);
          if (existing) {
            updated.set(event.toolUseId, {
              ...existing,
              inputPayload: event.inputPayload,
              queryLanguage: event.queryLanguage,
            });
          }
          return { ...prev, toolCalls: updated };
        });
      } else if (event.type === 'tool_call_result') {
        setStreaming((prev) => {
          if (!prev) return prev;
          const updated = new Map(prev.toolCalls);
          const existing = updated.get(event.toolUseId);
          if (existing) {
            updated.set(event.toolUseId, {
              ...existing,
              status: event.success ? 'success' : 'error',
              summary: event.summary,
              resultSummary: event.resultSummary,
              latencyMs: event.latencyMs,
            });
          }
          return { ...prev, toolCalls: updated };
        });
      } else if (event.type === 'stream_end') {
        setStreaming(null);
        void refetch();
      } else if (event.type === 'stream_error') {
        setStreaming(null);
        void refetch();
      } else if (event.type === 'typing_indicator') {
        setTypingUsers((prev) => {
          const updated = new Map(prev);
          if (event.isTyping) {
            updated.set(event.userId, event.userName);
          } else {
            updated.delete(event.userId);
          }
          return updated;
        });
      }
    });
    return unsub;
  }, [conversationId, refetch]);

  // Watchdog: if no streaming events for 15s while streaming, poll DB to check if message completed
  useEffect(() => {
    if (!streaming) return;
    const interval = setInterval(async () => {
      if (Date.now() - lastStreamEventRef.current > 15_000) {
        const result = await refetch();
        const msgs = result.data?.messages as Array<{ id: string; status?: string }> | undefined;
        const msg = msgs?.find((m) => m.id === streaming.messageId);
        if (msg && (msg.status === 'complete' || msg.status === 'error')) {
          setStreaming(null);
        }
      }
    }, 8_000);
    return () => clearInterval(interval);
  }, [streaming?.messageId, refetch]);

  // On WebSocket reconnect, check if active stream completed while disconnected
  useEffect(() => {
    return wsManager.onReconnect(() => {
      if (streaming) {
        void refetch().then((result) => {
          const msgs = result.data?.messages as Array<{ id: string; status?: string }> | undefined;
          const msg = msgs?.find((m) => m.id === streaming.messageId);
          if (msg && (msg.status === 'complete' || msg.status === 'error')) {
            setStreaming(null);
          }
        });
      }
    });
  }, [streaming?.messageId, refetch]);

  // Initial scroll: to unread divider or bottom (runs once per conversation)
  useEffect(() => {
    if (!data?.messages?.length || hasScrolledToUnread.current) return;
    // dividerMsgRef.current is set synchronously above, so it's always correct here
    if (dividerMsgRef.current) {
      const timer = setTimeout(() => {
        if (unreadRef.current) {
          unreadRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        } else {
          bottomRef.current?.scrollIntoView({ behavior: 'instant' });
        }
        hasScrolledToUnread.current = true;
      }, 100);
      return () => clearTimeout(timer);
    }
    bottomRef.current?.scrollIntoView({ behavior: 'instant' });
    hasScrolledToUnread.current = true;
  }, [data?.messages?.length]);

  // Auto-scroll to bottom for new messages, streaming, and typing (only after
  // initial scroll). If the user has scrolled up, leave them where they are
  // and surface a jump pill instead. Streaming fires this effect on every
  // text_delta — coalesce via rAF and use 'auto' (instant) instead of
  // 'smooth' so successive scrolls don't fight each other or stack up.
  const scrollScheduledRef = useRef(false);
  useEffect(() => {
    if (!hasScrolledToUnread.current) return;
    if (!isAtBottomRef.current) {
      setShowJumpToLatest(true);
      return;
    }
    if (scrollScheduledRef.current) return;
    scrollScheduledRef.current = true;
    requestAnimationFrame(() => {
      scrollScheduledRef.current = false;
      if (isAtBottomRef.current) {
        bottomRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    });
  }, [data?.messages?.length, streaming?.content, typingUsers.size]);

  // Mark conversation as read when viewing
  useEffect(() => {
    if (!data?.messages?.length || hasMarkedAsRead.current) return;
    const messages = data.messages as Array<{ id: string; status?: string }>;
    const lastComplete = [...messages].reverse().find(m => m.status !== 'streaming');
    if (lastComplete && lastComplete.id !== liveLastReadMessageId) {
      hasMarkedAsRead.current = true;
      markAsRead.mutateAsync({ conversationId, messageId: lastComplete.id }).then(() => {
        void utils.conversations.list.invalidate();
        setTimeout(() => setDividerCleared(true), 3000);
      }).catch(() => {});
    }
  }, [conversationId, data?.messages?.length]);

  const handleSendStep = useCallback(async (step: string) => {
    await sendMessage.mutateAsync({ conversationId, content: step, surface: 'web' });
    await utils.conversations.get.invalidate({ conversationId });
  }, [conversationId, sendMessage, utils]);

  const handleConfirmAction = useCallback(async (action: string) => {
    await sendMessage.mutateAsync({ conversationId, content: `Confirmed: ${action}`, surface: 'web' });
    await utils.conversations.get.invalidate({ conversationId });
  }, [conversationId, sendMessage, utils]);

  const handleCancelAction = useCallback(async () => {
    await sendMessage.mutateAsync({ conversationId, content: 'Cancelled', surface: 'web' });
    await utils.conversations.get.invalidate({ conversationId });
  }, [conversationId, sendMessage, utils]);

  const markdownComponents = useMemo(() => ({
    code: ({ children, className, ...props }: React.ComponentPropsWithoutRef<'code'> & { className?: string }) => {
      // Only transform inline code (block code has a className like "language-xxx")
      if (className) return <code className={className} {...props}>{children}</code>;
      const text = String(children);
      const textLower = text.toLowerCase();
      // Strip :port suffix from IPv4 addresses for fallback matching
      const ipWithoutPort = textLower.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}):\d{1,5}$/)?.[1] ?? null;

      let nodeId = graphNodeLabels.get(textLower);
      if (!nodeId && ipWithoutPort) nodeId = graphNodeLabels.get(ipWithoutPort);
      if (nodeId) {
        const entityInfo = graphEntityMap?.get(textLower) ?? (ipWithoutPort ? graphEntityMap?.get(ipWithoutPort) : undefined);
        return <EntityChip label={text} nodeId={nodeId} aliases={entityInfo?.aliases} entityType={entityInfo?.type} />;
      }
      // Check graphEntityMap for entity linking (WI-6)
      if (graphEntityMap) {
        let entity = graphEntityMap.get(textLower);
        if (!entity && ipWithoutPort) entity = graphEntityMap.get(ipWithoutPort);
        if (entity) {
          return (
            <span
              className={`entity-ref entity-ref-${entity.type}`}
              title={`Click to focus ${entity.type} on graph`}
              onClick={() => onEntityClick?.(entity.id)}
            >
              {text}
            </span>
          );
        }
      }
      return <code {...props}>{children}</code>;
    },
    // Evidence-table cells: severity → colored bar + mono label; time/IP → mono
    // tabular. Shared with StreamingMessage (see markdownCells.tsx).
    td: EvidenceTd,
  }), [graphNodeLabels, graphEntityMap, onEntityClick]);

  if (isLoading) {
    return (
      <div className="message-list">
        <MessageSkeleton />
      </div>
    );
  }

  const messages: Array<{
    id: string;
    senderType: string;
    senderId?: string | null;
    content: string | null;
    status?: string;
    createdAt?: string | Date;
    metadata?: Record<string, unknown>;
    sender?: { id: string; name: string } | null;
  }> = data?.messages ?? [];

  const lastCadeIdx = messages.reduce((acc, msg, i) => (msg.senderType !== 'user' ? i : acc), -1);

  // Empty conversation — show hero (same as homepage)
  if (messages.length === 0 && !streaming) {
    const userName = (data as { participants?: Array<{ user?: { name?: string } }> } | undefined)
      ?.participants?.[0]?.user?.name?.split(' ')[0] ?? '';
    return (
      <div className="home-screen home-screen--hero">
        <div className="home-hero">
          <CadeAvatar size={72} />
          {userName && <h1 className="home-hero-greeting">Hi, {userName}!</h1>}
        </div>
      </div>
    );
  }

  return (
    <div className="message-list" ref={setListRefCallback}>
      {messages.map((msg, idx) => {
        const isNote = !!(msg.metadata?.isNote);
        const isCade = msg.senderType !== 'user' && msg.senderType !== 'system';
        const isSystem = msg.senderType === 'system';
        const isOtherUser = !isCade && !isSystem && msg.sender?.id !== userId;
        // Skip the streaming placeholder
        if (isCade && streaming?.messageId === msg.id && msg.status === 'streaming') return null;

        // Parse persisted reasoning and tool calls from metadata
        const persistedTrace = (msg.metadata?.reasoningTrace as ReasoningStep[] | undefined) ?? [];
        const persistedToolCalls = (msg.metadata?.toolCalls as ToolCallRecord[] | undefined) ?? [];
        const hasPersistedTrace = persistedTrace.length > 0 || persistedToolCalls.length > 0;

        const writeAction = isCade && msg.content ? parseWriteAction(msg.content) : null;
        const isLast = idx === lastCadeIdx && !streaming;
        const nextStepsParsed = isLast && isCade && msg.content ? parseNextSteps(msg.content) : { steps: [], stripFrom: -1 };
        const nextSteps = nextStepsParsed.steps;
        // Drop the trailing next-steps list (shown as buttons) and the write-action block
        // (shown as the WriteConfirmation card) from the rendered markdown — no duplicates.
        const writeStripFrom = writeAction && isLast && isCade && msg.content ? msg.content.search(/\n*\*\*Write Action:\*\*/) : -1;
        const stripPoints = [nextStepsParsed.stripFrom, writeStripFrom].filter((n) => n >= 0);
        const cadeDisplayContent = isCade && msg.content && stripPoints.length
          ? msg.content.slice(0, Math.min(...stripPoints)).trimEnd()
          : msg.content;

        // Mentions attached to this message
        const msgMentions = (msg.metadata?.mentions as Array<{ userId: string; name: string }> | undefined) ?? [];

        // Note message — distinct rendering
        if (isNote) {
          const senderName = msg.sender?.name ?? 'Unknown';
          const isOwn = msg.sender?.id === userId;
          return (
            <div key={msg.id}>
              <div className={`message message-note ${isOwn ? 'message-note-own' : ''}`}>
                <div className="message-note-header">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="message-note-icon">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span className="message-note-sender">{isOwn ? 'You' : senderName}</span>
                  <span className="message-note-label">note</span>
                </div>
                <div className="message-note-content">
                  {renderNoteContent(msg.content ?? '', msgMentions)}
                </div>
              </div>
              {/* Unread messages divider */}
              {!dividerCleared && dividerMsgRef.current && msg.id === dividerMsgRef.current && idx < messages.length - 1 && (
                <div className="unread-divider" ref={unreadRef}>
                  <span className="unread-divider-text">New messages</span>
                </div>
              )}
            </div>
          );
        }

        return (
          <div key={msg.id}>
            <div className={`message ${isCade ? 'message-cade' : isOtherUser ? 'message-other-user' : 'message-user'}`}>
              {isCade && (
                <div className="message-header">
                  <CadeAvatar size={29} />
                  <span className="message-sender message-sender-cade">Cade</span>
                  {msg.createdAt && (
                    <span className="message-time">{formatMessageTime(msg.createdAt)}</span>
                  )}
                </div>
              )}
              {isOtherUser && (
                <div className="message-header">
                  <span className="message-sender message-sender-other">{msg.sender?.name ?? 'Unknown'}</span>
                </div>
              )}
              <div className="message-content">
                {/* Inline reasoning trace for persisted messages */}
                {isCade && hasPersistedTrace && (
                  <ReasoningTrace
                    persistedTrace={persistedTrace}
                    persistedToolCalls={persistedToolCalls}
                  />
                )}
                {msg.content ? (
                  isCade ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{cadeDisplayContent}</ReactMarkdown>
                  ) : (
                    (() => {
                      const { text, files } = parseAttachments(msg.content!);
                      const fileIds = (msg.metadata?.fileIds as string[] | undefined) ?? [];
                      return (
                        <>
                          {text && <span>{text}</span>}
                          {files.length > 0 && (
                            <div className="message-attachments">
                              {files.map((name, i) => (
                                <MessageAttachmentCard
                                  key={i}
                                  fileId={fileIds[i]}
                                  filename={name}
                                  onImagePreview={(url, fname) => setPreviewImage({ url, name: fname })}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()
                  )
                ) : (
                  isCade ? (
                    streaming?.messageId === msg.id && (streaming.reasoningText || streaming.toolCalls?.size) ? (
                      <ReasoningTrace
                        reasoningText={streaming.reasoningText}
                        toolCalls={streaming.toolCalls}
                        traceEntries={streaming.traceEntries}
                        isStreaming
                      />
                    ) : (
                      <div className="reasoning-inline">
                        <div className="reasoning-collapse-bar" style={{ cursor: 'default' }}>
                          <span className="reasoning-status-icon">
                            <span className="reasoning-status-spinner" />
                          </span>
                          <span className="reasoning-step-text streaming">Thinking…</span>
                        </div>
                      </div>
                    )
                  ) : null
                )}
              </div>
            </div>

            {/* Write confirmation */}
            {writeAction && isLast && (
              <WriteConfirmation
                action={writeAction.action}
                description={writeAction.description}
                target={writeAction.target}
                onConfirm={() => { void handleConfirmAction(writeAction.action); }}
                onCancel={() => { void handleCancelAction(); }}
              />
            )}

            {/* Next step buttons — only on the last cade message */}
            {nextSteps.length > 0 && (
              <NextStepButtons
                steps={nextSteps}
                onSelect={(step) => { void handleSendStep(step); }}
              />
            )}

            {/* Per-finding follow-up chips — let the user drill into a specific
                finding without advancing to the next phase. Rendered before the
                next-phase chip so the user reads findings → drills in OR advances. */}
            {isLast && isCade && Array.isArray(msg.metadata?.['followUps']) && (() => {
              const followUps = msg.metadata['followUps'] as Array<{ label: string; prompt: string }>;
              if (followUps.length === 0) return null;
              return (
                <NextStepButtons
                  steps={followUps.map((f) => f.label)}
                  onSelect={(label) => {
                    const match = followUps.find((f) => f.label === label);
                    if (match) void handleSendStep(match.prompt);
                  }}
                />
              );
            })()}

            {/* Next-phase chip (Entra Posture Assessment) — only on the last cade message,
                only if the orchestrator extracted a <next-phase> marker, and only if
                there's an actual next phase number (skip when number === "done"). */}
            {isLast && isCade && msg.metadata?.['nextPhase'] !== undefined && (() => {
              const np = msg.metadata['nextPhase'] as { number: number | 'done'; name?: string };
              if (np.number === 'done') return null;
              const label = np.name
                ? `Continue: Phase ${np.number} — ${np.name}`
                : `Continue: Phase ${np.number}`;
              const prompt = `Run posture phase ${np.number}`;
              return (
                <NextStepButtons
                  steps={[label]}
                  onSelect={() => { void handleSendStep(prompt); }}
                />
              );
            })()}


            {/* Unread messages divider */}
            {!dividerCleared && dividerMsgRef.current && msg.id === dividerMsgRef.current && idx < messages.length - 1 && (
              <div className="unread-divider" ref={unreadRef}>
                <span className="unread-divider-text">New messages</span>
              </div>
            )}
          </div>
        );
      })}

      {/* Streaming message with inline reasoning trace */}
      {streaming && !streaming.done && (
        <StreamingMessage
          content={streaming.content}
          reasoningText={streaming.reasoningText}
          toolCalls={streaming.toolCalls}
          traceEntries={streaming.traceEntries}
        />
      )}

      {/* Typing indicators */}
      {Array.from(typingUsers.entries()).map(([userId, userName]) => (
        <TypingIndicator key={userId} userName={userName} />
      ))}

      <div ref={bottomRef} />

      {showJumpToLatest && (
        <button
          type="button"
          className="jump-to-latest-pill"
          onClick={jumpToLatest}
          aria-label="Jump to latest messages"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <span>Jump to latest</span>
        </button>
      )}

      {previewImage && (
        <ImagePreviewModal
          url={previewImage.url}
          filename={previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
}
